/**
 * workAtoms — small presentational parts shared by the Work index + drill-in:
 * a phase badge, a priority chip, and a quiet mono-label dropdown (WkSelect).
 * All token-mapped; amber = needs-you, teal = in-flight, no alarm-red except
 * the genuine error/urgent token the design already used.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Check, ChevronDown, Zap } from "lucide-react";
import type { WorkItem, WorkItemPriority } from "@yagni/shared";
import { PHASE_LABELS, PRIORITY_LABELS } from "../lib/workItemConstants.js";
import { confidenceLabel } from "../lib/confidence.js";
import { readSplitPlace, splitWaitWords } from "./splitPlace.js";
import { workItemStatus, type WorkStatusTone } from "./workLifecycle.js";
import type { PillTone } from "../ui/PageCard.js";

/**
 * Provenance chip for non-user work: who put this on your desk, and how sure
 * they were. Renders nothing for user-created items — your own work needs no
 * byline. The confidence percent only shows when the creator recorded one.
 */
export function WkOrigin({ item }: { item: WorkItem }) {
  const origin = item.originType;
  if (!origin || origin === "user") return null;
  const label =
    origin === "agent" ? "by @yagni" : origin === "schedule" ? "scheduled" : "via webhook";
  const confidence =
    typeof item.confidence === "number" ? ` · ${confidenceLabel(item.confidence)}` : "";
  return (
    <span className="wk-origin" title="Who proposed this ticket">
      {label}
      {confidence}
    </span>
  );
}

export function WkPriority({ priority }: { priority: WorkItemPriority }) {
  if (priority === "normal") return null;
  return (
    <span className={`wk-pri wk-pri-${priority}`}>
      <Zap width={11} height={11} aria-hidden /> {PRIORITY_LABELS[priority]}
    </span>
  );
}

/** Short phase label for chips ("Proposal", "Executing", …). */
export function phaseLabel(item: WorkItem): string {
  return PHASE_LABELS[item.phase];
}

/**
 * One-word labels for the compact StatusPill, keyed to the {@link workItemStatus}
 * tone. The pill is a 9px uppercase nowrap chip, so it gets a TERSE word ("Parked",
 * "Working") — the full honest sentence ("Parked. Nothing scheduled.") stays for
 * the center prose. `planning` tone reads "Drafting" to match "Drafting plan".
 * A review case overrides these with its own words: "Reviewing" while its run
 * is live, "Reviewed" when idle — the commissioned "Working"/"Parked" would
 * misname what a reviewer does.
 */
const PILL_LABEL: Record<WorkStatusTone, string> = {
  needsYou: "Needs you",
  working: "Working",
  planning: "Drafting",
  scheduled: "Scheduled",
  parked: "Parked",
  done: "Done",
};

/**
 * The one PageCard pill an item wears, derived from {@link workItemStatus} so
 * the board, the list, and the drill-in all tell the same honest story (a
 * planning item reads "Drafting", not "Executing"). Maps the richer status tone
 * down to the PageCard pill vocabulary: live runs are teal `executing`,
 * scheduled/parked are quiet `neutral`. The label is a terse one-word form of
 * the tone — the full sentence lives only in the center prose.
 */
export function workItemPill(item: WorkItem): { label: string; tone: PillTone } {
  const status = workItemStatus(item);
  const { tone, label } = status;
  const pillTone: PillTone =
    tone === "needsYou"
      ? "needsYou"
      : tone === "working" || tone === "planning"
        ? "executing"
        : tone === "done"
          ? "done"
          : "neutral"; // scheduled | parked
  let word: string;
  if (label === "Queued for next pass") word = "Queued";
  else if (tone === "scheduled" && splitWaitWords(readSplitPlace(item))) word = "Waiting";
  else if (item.kind === "review" && tone === "working") word = "Reviewing";
  else if (item.kind === "review" && tone === "parked") word = "Reviewed";
  else if (tone === "done") word = label;
  else word = PILL_LABEL[tone];
  return { label: word, tone: pillTone };
}

export interface SelectOption {
  key: string;
  label: string;
  dot?: string;
  count?: number;
}

export interface WkSelectProps {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (key: string) => void;
}

/** A quiet dropdown: mono eyebrow label + current value + a small menu. */
export function WkSelect({ label, value, options, onChange }: WkSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const cur = options.find((o) => o.key === value) ?? options[0];

  return (
    <div className={"wk-select" + (open ? " open" : "")} ref={ref}>
      <button
        type="button"
        className="wk-select-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="wk-select-lab">{label}</span>
        {cur?.dot && <span className="wk-select-dot" style={{ background: cur.dot }} aria-hidden />}
        <span className="wk-select-val">{cur?.label}</span>
        <ChevronDown className="wk-select-chev" width={12} height={12} aria-hidden />
      </button>
      {open && (
        <div className="wk-menu" role="listbox">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              role="option"
              aria-selected={o.key === value}
              className={"wk-menu-item" + (o.key === value ? " on" : "")}
              onClick={() => {
                onChange(o.key);
                setOpen(false);
              }}
            >
              {o.dot ? (
                <span className="wk-menu-dot" style={{ background: o.dot }} aria-hidden />
              ) : (
                <span className="wk-menu-dot ghost" aria-hidden />
              )}
              <span className="wk-menu-item-lab">{o.label}</span>
              {typeof o.count === "number" && <span className="wk-menu-count">{o.count}</span>}
              {o.key === value && (
                <Check className="wk-menu-check" width={13} height={13} aria-hidden />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Stamp the inline `--dc` accent var on a wrapper. */
export function accentStyle(accent: string): CSSProperties {
  return { "--dc": accent } as CSSProperties;
}
